import React, { useCallback, useEffect } from 'react';
import { createContext } from '@shared/utils/createContext';
import { useDisclosure } from '@withjoy/joykit';
import { useCatalogContext } from '../../state/Catalog';
import { PagesForTelemetry, SubmitMethod } from '../..';

import { useSearchTermQueryParam } from '../../utils/queryParams';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useCatalogLoggingContext } from '../../Catalog.logging';

type CatalogSearchBarContext = {
  isOpen: boolean;
  searchQuery: string;

  handleOnSearchBarFocused: (page: PagesForTelemetry) => void;
  handleOnChangeSearchQuery: (search: string) => void;
  handleOnClearSearchQuery: (args: { source: 'button'; page: PagesForTelemetry; method: SubmitMethod }) => void;
  handleOnOpen: (args: { source: 'modifyButton' | 'searchIcon'; page: PagesForTelemetry }) => void;
  handleOnClose: (args: { source: 'overlayClick' | 'cancelButton'; page: PagesForTelemetry }) => void;
  handleOnSubmitSearch: (args: { page: PagesForTelemetry; method: SubmitMethod }) => void;
  handleOnPaste: (args: { page: PagesForTelemetry; event: React.ClipboardEvent<HTMLInputElement> }) => void;
  onCloseEditGiftDialog: () => void;
  isOpenEditGiftDialog: boolean;
  handleOnSelectOptionSearchQuery: (option: string) => void;
};

const NON_JOY_URL_VALIDATION_REGEX = new RegExp('^(https?://(?!.*withjoy.com).+)', 'i');

const OPEN_SOURCE_TO_COMPONENT = {
  modifyButton: 'modify',
  searchIcon: 'search icon',
} as const;

const CLOSE_SOURCE_TO_COMPONENT = {
  overlayClick: 'click outside',
  cancelButton: 'button',
} as const;

const [Provider, useCatalogSearchBarContext] = createContext<CatalogSearchBarContext>({ name: 'CatalogSearchBarProvider' });

interface CatalogSearchBarProviderProps
  extends Readonly<{
    children: React.ReactNode;
    searchQuery: string;
    setSearchQuery: (searchQuery: string) => void;
  }> {}

const CatalogSearchBarProvider = (props: CatalogSearchBarProviderProps) => {
  const { children, searchQuery, setSearchQuery } = props;

  const searchTermFromUrl = useSearchTermQueryParam() || '';

  const { goToRootCatalog, eventId } = useCatalogContext();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { onClose: onCloseEditGiftDialog, onOpen: onOpenEditGiftDialog, isOpen: isOpenEditGiftDialog } = useDisclosure();

  const canOpenEditGiftDialog = !!eventId;

  const { trackOpenSearchCatalogClicked, trackCancelSearchCatalogClicked, trackClearSearchCatalogClicked, trackSearchCatalogClicked, trackSearchBarFocused } =
    useCatalogLoggingContext();

  useEffect(() => {
    // Let the search query param drive component state.
    setSearchQuery(searchTermFromUrl);
  }, [searchTermFromUrl, setSearchQuery]);

  useEffect(() => {
    if (!isOpen) {
      // Ensure that local component state reflects search query param
      setSearchQuery(searchTermFromUrl);
    }
  }, [isOpen, searchTermFromUrl, setSearchQuery]);

  const handleOnOpen = useCallback<CatalogSearchBarContext['handleOnOpen']>(
    args => {
      const { page, source } = args;
      trackOpenSearchCatalogClicked({ page, component: OPEN_SOURCE_TO_COMPONENT[source], searchTerm: searchQuery });
      onOpen();
    },
    [onOpen, searchQuery, trackOpenSearchCatalogClicked],
  );

  const handleOnClose = useCallback<CatalogSearchBarContext['handleOnClose']>(
    args => {
      const { page, source } = args;
      trackCancelSearchCatalogClicked({ searchTerm: searchQuery, page, component: CLOSE_SOURCE_TO_COMPONENT[source] });
      onClose();
    },
    [onClose, searchQuery, trackCancelSearchCatalogClicked],
  );

  const handleOnSubmitSearch = useCallback<CatalogSearchBarContext['handleOnSubmitSearch']>(
    args => {
      const { page, method } = args;
      onClose();
      if (canOpenEditGiftDialog && NON_JOY_URL_VALIDATION_REGEX.test(searchQuery)) {
        trackSearchCatalogClicked({ page, searchTerm: searchQuery, method, isUrl: true });
        onOpenEditGiftDialog();
      } else if (!!searchQuery.length) {
        trackSearchCatalogClicked({ page, searchTerm: searchQuery, method });
        goToRootCatalog(`s=${encodeURIComponent(searchQuery)}`);
      } else {
        goToRootCatalog();
      }
    },
    [goToRootCatalog, onClose, searchQuery, trackSearchCatalogClicked, canOpenEditGiftDialog, onOpenEditGiftDialog],
  );

  const handleOnClearSearchQuery = useCallback<CatalogSearchBarContext['handleOnClearSearchQuery']>(
    args => {
      const { page, source } = args;
      trackClearSearchCatalogClicked({ page, searchTerm: searchQuery, component: source });
      setSearchQuery('');
      goToRootCatalog();
    },
    [trackClearSearchCatalogClicked, searchQuery, goToRootCatalog, setSearchQuery],
  );

  const handleOnChangeSearchQuery = useCallback<CatalogSearchBarContext['handleOnChangeSearchQuery']>(
    newQuery => {
      setSearchQuery(newQuery);
    },
    [setSearchQuery],
  );

  const handleOnSelectOptionSearchQuery = useCallback<CatalogSearchBarContext['handleOnSelectOptionSearchQuery']>(
    searchQuery => {
      setSearchQuery(searchQuery);
      goToRootCatalog(`s=${encodeURIComponent(searchQuery)}`);
    },
    [goToRootCatalog, setSearchQuery],
  );

  const handleOnSearchBarFocused = useEventCallback<CatalogSearchBarContext['handleOnSearchBarFocused']>(page => {
    trackSearchBarFocused({ page });
  });

  const handleOnPaste = useEventCallback<CatalogSearchBarContext['handleOnPaste']>(args => {
    const { page, event } = args;
    if (canOpenEditGiftDialog && NON_JOY_URL_VALIDATION_REGEX.test(event.clipboardData.getData('text'))) {
      trackSearchCatalogClicked({ page, searchTerm: searchQuery, method: 'onPaste', isUrl: true });
      onOpenEditGiftDialog();
    }
  });

  return (
    <Provider
      value={{
        isOpen,
        handleOnChangeSearchQuery,
        handleOnClearSearchQuery,
        handleOnOpen,
        handleOnClose,
        handleOnSearchBarFocused,
        handleOnSubmitSearch,
        searchQuery,
        handleOnPaste,
        onCloseEditGiftDialog,
        handleOnSelectOptionSearchQuery,
        isOpenEditGiftDialog,
      }}
    >
      {children}
    </Provider>
  );
};

CatalogSearchBarProvider.displayName = 'CatalogSearchBarProvider';

export { CatalogSearchBarProvider, useCatalogSearchBarContext };
